import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Button, Typography } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import { renderNotice } from '@/pages/catalogImport';
import { ProductDetailSectionHeading } from '../ProductDetailSectionHeading';
import { asListItem } from '../productDetailTypography';
import type { DraftPublisher } from './useDraftPublish';

type ProductDraftPanelProps = {
    /** The draft's issues, its Publish and the last refusal. */
    publisher: DraftPublisher;
    /** The header's Edit, offered when the product needs a category first. */
    onEdit: () => void;
};

/**
 * A draft's panel on the product page (bos-app#225, US-18, AC-17), under the
 * header: the state in a warning notice with **Publish**, a refused publish
 * in a danger notice (with Edit when it needs a category), then what the import could not fix, one sentence per
 * `draftIssues` entry after its rows, followed by what the import left out
 * of it (a dropped price, attributes, a barcode, a cut text). The parent renders it only while the
 * product is a draft. Not on the canvas yet; the placement is this child's.
 */
export const ProductDraftPanel: React.FC<ProductDraftPanelProps> = ({
    publisher,
    onEdit,
}) => {
    const { t } = useTranslation('pages');
    const { issues, isPublishing, refusal, onPublish } = publisher;

    return (
        <Widget
            className="flex flex-col gap-4 p-5"
            {...getNodeTestIdProp('product-detail-draft')}
        >
            <Alert status="warning">
                <Alert.Indicator />
                <Alert.Content>
                    <Alert.Title>
                        {t('products.detail.draft.title')}
                    </Alert.Title>
                    <Alert.Description>
                        {t('products.detail.draft.description')}
                    </Alert.Description>
                </Alert.Content>
                <Button
                    isPending={isPublishing}
                    onPress={onPublish}
                    variant="primary"
                    {...getNodeTestIdProp('product-detail-draft-publish')}
                >
                    <Icon icon="gravity-ui:circle-check" />
                    {isPublishing
                        ? t('products.detail.draft.publishing')
                        : t('products.detail.draft.publish')}
                </Button>
            </Alert>

            {refusal === null ? null : (
                <Alert
                    role="alert"
                    status="danger"
                    {...getNodeTestIdProp('product-detail-draft-refusal')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('products.detail.draft.refusedTitle')}
                        </Alert.Title>
                        <Alert.Description>
                            {refusal.kind === 'noCategory'
                                ? t('products.detail.draft.refusedNoCategory')
                                : refusal.kind === 'unpricedVariant'
                                  ? t('products.detail.draft.refusedVariant', {
                                        uniqueKey: refusal.uniqueKey,
                                    })
                                  : t('products.detail.draft.refused')}
                        </Alert.Description>
                    </Alert.Content>
                    {refusal.kind === 'noCategory' ? (
                        <Button
                            onPress={onEdit}
                            variant="outline"
                            {...getNodeTestIdProp(
                                'product-detail-draft-refusal-edit',
                            )}
                        >
                            <Icon icon="gravity-ui:pencil" />
                            {t('products.detail.edit')}
                        </Button>
                    ) : null}
                </Alert>
            )}

            <div className="flex flex-col gap-2">
                <ProductDetailSectionHeading>
                    {t('products.detail.draft.issuesTitle')}
                </ProductDetailSectionHeading>
                {issues.length === 0 ? (
                    <Typography
                        color="muted"
                        type="body-sm"
                        {...getNodeTestIdProp('product-detail-draft-no-issues')}
                    >
                        {t('products.detail.draft.noIssues')}
                    </Typography>
                ) : (
                    <ul
                        aria-label={t('products.detail.draft.issuesTitle')}
                        className="flex flex-col gap-1"
                    >
                        {issues.map((issue) => {
                            const sentence = [
                                renderNotice(t, issue.notice),
                                ...issue.kept.map((kept) =>
                                    t(kept.key, kept.params),
                                ),
                            ].join(' ');
                            return (
                                <Typography
                                    key={issue.id}
                                    className="wrap-anywhere"
                                    render={asListItem}
                                    type="body-sm"
                                    {...getNodeTestIdProp(
                                        `product-detail-draft-issue-${issue.id}`,
                                    )}
                                >
                                    {issue.rowCount === 0
                                        ? sentence
                                        : t('products.detail.draft.issue', {
                                              count: issue.rowCount,
                                              rows: issue.rows,
                                              sentence,
                                          })}
                                </Typography>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Widget>
    );
};
